"use client";

import { useState } from "react";

import dynamic from "next/dynamic";

import { useUser } from "@/contexts/user-provider";

import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";

// The tiptap/react-email composer chain is heavy and only needed once the email
// is actually clicked — load the drawer on demand instead of shipping it with
// every clickable email. No `loading` fallback: the drawer is an overlay, so a
// fallback here would render inline at this button's position in the page (an
// ugly skeleton injected into the patient card) while the chunk loads. The
// drawer shows its own skeleton inside the DrawerBody once mounted.
const PatientEmailComposeDrawer = dynamic(
  () => import("./patient-email-compose-drawer"),
  { ssr: false },
);

interface ClickableEmailProps {
  email: string;
  patientId: string;
  className?: string;
}

export default function ClickableEmail({
  email,
  patientId,
  className,
}: ClickableEmailProps) {
  const { currentUser } = useUser();
  const [open, setOpen] = useState(false);
  // Counts opens: 0 means "never clicked", so the composer chunk isn't fetched
  // up front. It doubles as the drawer's key, so every open mounts a fresh
  // composer — the drawer holds the subject/body/Cc/Bcc in local state and has
  // no reset, so without this a reopen (especially right after a send) would
  // resurrect the previous message. The key only changes on open, so the
  // instance survives the close and its slide-out animation still plays.
  const [openCount, setOpenCount] = useState(0);

  const canSendEmail = hasPermission(
    currentUser,
    PERMISSIONS.MESSAGING_EMAILS_WRITE,
  );

  if (!canSendEmail) {
    return (
      <a
        href={`mailto:${email}`}
        onClick={(e) => e.stopPropagation()}
        className={cx(clickableText, "min-w-0 truncate", className)}
      >
        {email}
      </a>
    );
  }

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpenCount((count) => count + 1);
          setOpen(true);
        }}
        className={cx(clickableText, "min-w-0 truncate", className)}
      >
        {email}
      </button>
      {openCount > 0 && (
        <PatientEmailComposeDrawer
          key={openCount}
          open={open}
          onOpenChange={setOpen}
          recipientEmail={email}
          patientId={patientId}
        />
      )}
    </>
  );
}
